import { packTile } from "../document/packed-tile.ts";
import { hashWords } from "../random.ts";
import { extendingColumn, isTileId } from "./face-columns.ts";
import { compileSides, drawSides, sidesProblem, type CompiledSide, type SideColumnRef } from "./face-sides.ts";
import type { CellRect } from "./match.ts";
import type { TerrainDefinition, TerrainFace } from "./types.ts";

export { faceTiles, type CompiledSide, type SideColumnRef } from "./face-sides.ts";

/**
 * Taller faces (ADR-072): after a terrain's last rule stage, every column of the rules' output that matches
 * a declared face column top to bottom is drawn `faceHeight` rows tall by repeating the column's repeating
 * row, when every cell the extension adds lies inside the map and is empty in the rules' output. The pass
 * reads only the rules' output and writes a separate buffer, so the order columns are visited in changes
 * nothing, and a column that does not fit keeps the face the rules drew. Side columns beside a face (ADR-072's
 * amendment) are drawn after every face column, as tall as the face they follow, their cells replacing a
 * face's where both write.
 */

/**
 * The tallest face a terrain layer may ask for, in rows.
 */
export const FACE_HEIGHT_MAX = 16;

const MIN_FACE_ROWS = 3;
const HASH_INPUTS = 5;

/**
 * A terrain whose face declaration cannot be used, and why.
 */
export interface TerrainFaceIssue {
  readonly terrain: string;
  readonly reason: string;
}

/**
 * A face declaration packed against a tileset for the pass: every column's packed cells in one array, the
 * columns each packed lip starts, each column's group, each group's distinct repeating-row tiles in
 * declaration order, and the side columns beside the face with the side columns each packed lip starts.
 */
export interface CompiledFace {
  readonly rows: number;
  readonly repeatRow: number;
  readonly columns: Uint32Array;
  readonly columnGroup: Uint32Array;
  readonly lips: ReadonlyMap<number, readonly number[]>;
  readonly repeats: readonly Uint32Array[];
  readonly sides: readonly CompiledSide[];
  readonly sideLips: ReadonlyMap<number, readonly SideColumnRef[]>;
}

/**
 * What the pass reads and where it writes. `source` is the rules' output and is never written; `output` is
 * the caller's buffer of the same size. `stageIndex` is the hash word that keeps the pass's draws apart from
 * every rule's: `stageIndexBase + stageCount`, the rule set's number of stages past the caller's base.
 */
export interface FaceInput {
  readonly face: CompiledFace;
  readonly source: Readonly<Uint32Array>;
  readonly output: Uint32Array;
  readonly width: number;
  readonly height: number;
  readonly faceHeight: number;
  readonly seed: number;
  readonly layerIndex: number;
  readonly stageIndex: number;
}

/**
 * A restricted run, as `applyRuleStage` takes it: `rect` bounds the lip positions scanned, `writeRect` the
 * cells written, the output being reset to the rules' output over `writeRect` first.
 */
export interface FaceOptions {
  readonly rect?: CellRect;
  readonly writeRect?: CellRect;
}

/**
 * The number of rows a face declares: the length of its columns, the family's own height.
 *
 * @param face The declaration.
 * @returns The rows, 0 for a declaration with no column.
 */
export function faceRows(face: TerrainFace): number {
  return face.groups[0]?.[0]?.length ?? 0;
}

/**
 * The first problem with a face declaration's shape, or null when it is well formed.
 *
 * @param face The declaration.
 * @returns The reason, or null.
 */
function faceProblem(face: TerrainFace): string | null {
  if (face.groups.length === 0) {
    return "the face declares no column group";
  }
  const empty = face.groups.findIndex((group) => group.length === 0);
  if (empty >= 0) {
    return `group ${empty} has no column`;
  }
  const rows = faceRows(face);
  for (const [g, group] of face.groups.entries()) {
    for (const [c, column] of group.entries()) {
      if (column.length !== rows) {
        return `column ${c} of group ${g} has ${column.length} rows where the first column has ${rows}`;
      }
    }
  }
  if (rows < MIN_FACE_ROWS) {
    return `columns have ${rows} rows; a face needs at least ${MIN_FACE_ROWS} rows`;
  }
  if (!Number.isInteger(face.repeatRow) || face.repeatRow <= 0 || face.repeatRow >= rows - 1) {
    return `repeatRow ${face.repeatRow} is not a row strictly between the first and the last of ${rows}`;
  }
  const seen = new Set<string>();
  for (const [g, group] of face.groups.entries()) {
    for (const [c, column] of group.entries()) {
      const bad = column.find((tile) => !isTileId(tile));
      if (bad !== undefined) {
        return `tile ${bad} in column ${c} of group ${g} is not a tile id`;
      }
      const key = column.join("/");
      if (seen.has(key)) {
        return `column ${key} appears more than once`;
      }
      seen.add(key);
    }
  }
  return sidesProblem(face, rows, seen);
}

/**
 * Checks every face declaration among the terrains: at least one group, no empty group, every column the
 * same length and at least three rows long, `repeatRow` strictly between the first and the last row, every
 * id a local tile id, no column declared twice, every side well formed (ADR-072's amendment), and a rule set
 * on the terrain, since a face is found in the rules' output. Whether an id lies inside its tileset is the context builder's check, which knows tile
 * counts.
 *
 * @param terrains The terrains.
 * @returns One issue per terrain whose declaration fails, in order; empty when all are usable.
 */
export function validateTerrainFaces(terrains: readonly TerrainDefinition[]): TerrainFaceIssue[] {
  const issues: TerrainFaceIssue[] = [];
  for (const terrain of terrains) {
    if (terrain.face === undefined) {
      continue;
    }
    const problem = faceProblem(terrain.face) ?? (terrain.ruleSet === undefined ? "the terrain declares a face but binds no rule set, and a face is found in the rules' output" : null);
    if (problem !== null) {
      issues.push({ terrain: terrain.id, reason: problem });
    }
  }
  return issues;
}

/**
 * Packs a face declaration against the tileset index its terrain's tiles take in a world.
 *
 * @param face The declaration.
 * @param tilesetIndex The terrain tileset's index in the world's tilesets.
 * @returns The compiled face.
 * @throws Error when the declaration is malformed; RangeError when the tileset index cannot be packed.
 */
export function compileFace(face: TerrainFace, tilesetIndex: number): CompiledFace {
  const problem = faceProblem(face);
  if (problem !== null) {
    throw new Error(`face declaration is malformed: ${problem}`);
  }
  const rows = faceRows(face);
  const all = face.groups.flat();
  const columns = new Uint32Array(all.length * rows);
  const columnGroup = new Uint32Array(all.length);
  const lips = new Map<number, number[]>();
  const repeats: Uint32Array[] = [];
  let index = 0;
  face.groups.forEach((group, g) => {
    const distinct: number[] = [];
    for (const column of group) {
      column.forEach((tile, r) => {
        columns[index * rows + r] = packTile(tilesetIndex, tile);
      });
      columnGroup[index] = g;
      const lip = columns[index * rows] as number;
      lips.set(lip, [...(lips.get(lip) ?? []), index]);
      const repeat = columns[index * rows + face.repeatRow] as number;
      if (!distinct.includes(repeat)) {
        distinct.push(repeat);
      }
      index += 1;
    }
    repeats.push(Uint32Array.from(distinct));
  });
  const { sides, sideLips } = compileSides(face, rows, tilesetIndex);
  return { rows, repeatRow: face.repeatRow, columns, columnGroup, lips, repeats, sides, sideLips };
}

/**
 * Draws the declared faces of the rules' output `faceHeight` rows tall into the caller's buffer. The rows
 * above the repeating row stay as the rules drew them, `faceHeight` less the face's rows are added below
 * it, each picked among the repeating-row tiles of the matched column's group by
 * `hashWords(seed, [x, y, layerIndex, stageIndex, 0], 5)` (a group with one such tile takes it without a
 * hash), and the rows below it move down by as many. A column is extended only when every cell it adds, down
 * to the moved foot, lies inside the map and is empty in the rules' output. The face's side columns are
 * drawn after every face column (`drawSides`), their cells replacing a face's. At the face's own height the
 * output is the rules' output over the rectangle written and nothing is hashed.
 *
 * @param input The rules' output, the buffer to write, the face, the height and the hash words.
 * @param options `rect` restricts the lip positions scanned and `writeRect` the cells written (default the
 *   whole map); the output is reset to the rules' output over `writeRect` before anything is added.
 * @throws RangeError when a buffer does not hold `width * height` cells or the height is not an integer
 *   from the face's rows to `FACE_HEIGHT_MAX`.
 */
export function extendFaces(input: FaceInput, options: FaceOptions = {}): void {
  const { face, source, output, width, height, faceHeight } = input;
  const cells = width * height;
  if (source.length !== cells || output.length !== cells) {
    throw new RangeError(`the face pass needs ${cells} cells in its source and output, received ${source.length} and ${output.length}`);
  }
  if (!Number.isInteger(faceHeight) || faceHeight < face.rows || faceHeight > FACE_HEIGHT_MAX) {
    throw new RangeError(`face height ${faceHeight} is not an integer from ${face.rows} to ${FACE_HEIGHT_MAX}`);
  }
  const clip = options.writeRect ?? { x0: 0, y0: 0, x1: width, y1: height };
  const cx0 = Math.max(0, clip.x0);
  const cy0 = Math.max(0, clip.y0);
  const cx1 = Math.min(width, clip.x1);
  const cy1 = Math.min(height, clip.y1);
  if (options.writeRect === undefined) {
    output.set(source);
  } else {
    for (let y = cy0; y < cy1; y += 1) {
      output.set(source.subarray(y * width + cx0, y * width + Math.max(cx0, cx1)), y * width + cx0);
    }
  }
  const extra = faceHeight - face.rows;
  if (extra === 0) {
    return;
  }
  const rect = options.rect ?? { x0: 0, y0: 0, x1: width, y1: height };
  const scan = { x0: Math.max(0, rect.x0), y0: Math.max(0, rect.y0), x1: Math.min(rect.x1, width), y1: rect.y1 };
  const scratch = new Uint32Array(HASH_INPUTS);
  scratch[2] = input.layerIndex;
  scratch[3] = input.stageIndex;
  const yEnd = Math.min(scan.y1, height - faceHeight + 1);
  for (let y = scan.y0; y < yEnd; y += 1) {
    for (let x = Math.max(scan.x0, cx0); x < Math.min(scan.x1, cx1); x += 1) {
      const c = extendingColumn(face, source, width, height, x, y, faceHeight);
      if (c < 0) {
        continue;
      }
      const repeats = face.repeats[face.columnGroup[c] as number] as Uint32Array;
      for (let r = 1; r <= extra; r += 1) {
        const yy = y + face.repeatRow + r;
        if (yy >= cy0 && yy < cy1) {
          scratch[0] = x;
          scratch[1] = yy;
          output[yy * width + x] = repeats.length === 1 ? (repeats[0] as number) : (repeats[hashWords(input.seed, scratch, HASH_INPUTS) % repeats.length] as number);
        }
      }
      for (let r = face.repeatRow + 1; r < face.rows; r += 1) {
        const yy = y + r + extra;
        if (yy >= cy0 && yy < cy1) {
          output[yy * width + x] = face.columns[c * face.rows + r] as number;
        }
      }
    }
  }
  if (face.sides.length > 0) {
    drawSides(face, source, output, width, height, faceHeight, scan, { x0: cx0, y0: cy0, x1: cx1, y1: cy1 });
  }
}
